// Implements: F-IDN-012, F-IDN-015
/**
 * The moments of the account pages as text, in the platform locale (`types/locale.ts`) and the
 * reader's zone, and never as an exception: `Intl.DateTimeFormat.format` throws on an unparsable
 * value, and one malformed field must not blank the page. The raw value is shown instead, and a
 * missing one as `absent`.
 */

import { PLATFORM_HOUR_CYCLE, PLATFORM_LOCALE } from "../../../types/locale";

/** "12 oct. 2026": the date of a badge ("Activada desde …"). */
const SHORT_DATE = new Intl.DateTimeFormat(PLATFORM_LOCALE, { day: "numeric", month: "short", year: "numeric" });
/** "12 de enero de 2026": the date of a sentence ("En Valtx desde el …"). */
const LONG_DATE = new Intl.DateTimeFormat(PLATFORM_LOCALE, { day: "numeric", month: "long", year: "numeric" });
/** "12 oct. 2026, 18:40": a moment in a table or a detail list. */
const MOMENT = new Intl.DateTimeFormat(PLATFORM_LOCALE, {
  dateStyle: "medium",
  timeStyle: "short",
  hourCycle: PLATFORM_HOUR_CYCLE,
});
/** "18:40": the time of today's confirmation. */
const TIME = new Intl.DateTimeFormat(PLATFORM_LOCALE, {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: PLATFORM_HOUR_CYCLE,
});

export type DateStyle = "short" | "long" | "moment" | "time";

const FORMATS: Readonly<Record<DateStyle, Intl.DateTimeFormat>> = {
  short: SHORT_DATE,
  long: LONG_DATE,
  moment: MOMENT,
  time: TIME,
};

/**
 * v3 "Último uso" (Default P1-3: the time only, no place): "Hoy, 10:24" for a moment of the reader's
 * own today, the full moment otherwise, `null` for none (a field an older server never sent included).
 * An unparsable value reads as itself.
 */
export function lastUseText(raw: string | null | undefined, now: Date, today: (time: string) => string): string | null {
  if (raw === null || raw === undefined || raw.trim() === "") return null;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  const sameDay =
    parsed.getFullYear() === now.getFullYear() && parsed.getMonth() === now.getMonth() && parsed.getDate() === now.getDate();
  return sameDay ? today(TIME.format(parsed)) : MOMENT.format(parsed);
}

export function dateText(raw: string | Date | null | undefined, style: DateStyle, absent = ""): string {
  if (raw === null || raw === undefined) return absent;
  if (typeof raw === "string" && raw.trim() === "") return absent;
  const parsed = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(parsed.getTime())) return typeof raw === "string" ? raw : absent;
  return FORMATS[style].format(parsed);
}
